import { zodResolver } from "@hookform/resolvers/zod";
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useSendFeedback } from "@/hooks/useSendFeedback";
import { toast } from "sonner";
import { X } from "lucide-react";
import { FeedbackType } from "@/api/feedback";

interface FeedbackDialogProps {
  children: React.ReactNode;
  type: FeedbackType;
}

export const FeedbackDialog: React.FC<FeedbackDialogProps> = ({
  children,
  type,
}) => {
  const { user } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [imagesPreviews, setImagesPreviews] = useState<string[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const feedbackSchema = z.object({
    email: z.string().email("Invalid email address"),
    title: z.string().min(10).max(100),
    message: z
      .string()
      .min(10, "Message must be at least 10 characters long")
      .max(500, "Message must be at most 500 characters long"),
    images: z.array(z.instanceof(File)).max(5).optional(),
    fileInput: z.any().optional(),
    type: z.enum(["feedback", "bug"]),
  });

  const form = useForm({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      email: (user ? user.email : "") as string,
      title: "",
      message: "",
      images: [],
      fileInput: undefined,
      type,
    },
  });

  const { mutateAsync, isPending } = useSendFeedback();

  useEffect(() => {
    if (open) {
      form.reset({
        email: user?.email || "",
        title: "",
        message: "",
        images: [],
        type,
      });
      setImagesPreviews([]);
      setUploadedFiles([]);
    }
  }, [open, user, form, type]);

  const validateFile = (
    file: File,
  ): { valid: boolean; errorMessage?: string } => {
    const maxFileSize = 5 * 1024 * 1024; // 5MB
    const acceptedTypes = ["image/jpeg", "image/jpg", "image/png"];

    if (file.size > maxFileSize) {
      return {
        valid: false,
        errorMessage: `File ${file.name} is too large. Maximum size is 5MB.`,
      };
    }

    if (!acceptedTypes.includes(file.type)) {
      return {
        valid: false,
        errorMessage: `Invalid file type for ${file.name}. Only JPEG, JPG, and PNG are allowed.`,
      };
    }

    return { valid: true };
  };

  const fileToPreview = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result as string;
        resolve(base64);
      };
      reader.onerror = () => {
        reject(new Error("Failed to convert file to preview"));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (files: FileList | null) => {
    if (!files) return;

    const currentFiles = uploadedFiles.length;
    if (currentFiles + files.length > 5) {
      toast.error("You can only upload up to 5 images");
      return;
    }

    for (const file of Array.from(files)) {
      const validation = validateFile(file);
      if (!validation.valid) {
        if (validation.errorMessage) {
          toast.error(validation.errorMessage);
        }
        continue;
      }

      try {
        const preview = await fileToPreview(file);
        setImagesPreviews((prev) => [...prev, preview]);
        setUploadedFiles((prev) => [...prev, file]);

        const currentImages = form.getValues("images") || [];
        form.setValue("images", [...currentImages, file] as any);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Error occurred";
        toast.error(errorMessage);
      }
    }

    form.setValue("fileInput", undefined as any);
  };

  const removeImage = (index: number) => {
    setImagesPreviews((prev) => prev.filter((_, i) => i !== index));
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));

    const currentImages = form.getValues("images") || [];
    form.setValue("images", currentImages.filter((_, i) => i !== index) as any);
  };

  const onSubmit = async (data: z.infer<typeof feedbackSchema>) => {
    await mutateAsync(data, {
      onSuccess() {
        toast("Feedback has been sent. Thank you!");
      },
    });
    form.reset();
    setImagesPreviews([]);
    setUploadedFiles([]);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogTitle>
          {type === "bug" ? "Report a Bug" : "Quick Feedback"}
        </DialogTitle>
        <DialogDescription>
          {type === "bug"
            ? "Tell us about an issue you encountered. We appreciate your help in improving our platform."
            : "Share your thoughts, ideas or suggestions. It only takes a few seconds."}
        </DialogDescription>
        <Form {...form}>
          <form
            className="space-y-3"
            onSubmit={form.handleSubmit(
              async (values) => await onSubmit(values),
            )}
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Your Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="Enter your email here..."
                      disabled={isPending}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Feedback Title</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder="Enter title here..."
                      disabled={isPending}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Your Feedback</FormLabel>
                  <FormControl>
                    <Textarea
                      className="resize-none"
                      placeholder="Write your feedback here..."
                      disabled={isPending}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="fileInput"
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel>Upload Screenshots (Optional)</FormLabel>
                    <span className="text-xs text-muted-foreground">
                      {imagesPreviews.length}/5 images
                    </span>
                  </div>
                  <FormControl>
                    <Input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      disabled={isPending || uploadedFiles.length >= 5}
                      multiple
                      onChange={(e) => {
                        onChange(e.target.files);
                        handleImageUpload(e.target.files);
                      }}
                      {...fieldProps}
                    />
                  </FormControl>

                  {imagesPreviews.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {imagesPreviews.map((preview, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={preview}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-auto rounded-md border border-border aspect-square object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-1 right-1 bg-background/80 rounded-full p-1 opacity-70 hover:opacity-100"
                            aria-label={`Remove image ${index + 1}`}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </FormItem>
              )}
            />

            <Button disabled={isPending}>
              {isPending ? "Sending..." : "Send"}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
